import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiError } from '@/api/types';
import { t } from '@/i18n';
import { noticeOf, type Notice } from '@/lib/errors';
import { createBridge, UploadCancelled, type CreateBridgeInput, type Kind } from '@/services/createBridge';
import { flowOf } from '@/services/homeKinds';

/** `creating`: the bridge is being made in the background while the share screen is already in place. */
export type Stage = 'input' | 'creating';

function describeError(error: unknown, action: string): Notice {
  if (!(error instanceof ApiError) && error instanceof Error) return { title: error.message };
  return noticeOf(error, t('createBridge.failed'), action);
}

type Draft = Pick<CreateBridgeInput, 'secretText' | 'file' | 'ttl' | 'views'>;

/** The creation of a bridge as the home screen shows it: its stage, the guide's step, the upload and what went wrong. */
export function useCreateBridge(kind: Kind | null, draft: Draft, ready: boolean) {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('input');
  const [createStep, setCreateStep] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<Notice | null>(null);
  // set by the upload's cancel button; the upload stops before its next chunk
  const cancelRequested = useRef(false);
  const { secretText, file, ttl, views } = draft;

  const create = useCallback(async () => {
    if (kind === null || stage !== 'input' || !ready) return;
    setError(null);
    cancelRequested.current = false;
    setStage('creating');
    setCreateStep(0);
    try {
      const path = await createBridge({
        kind,
        secretText,
        file,
        ttl,
        views,
        onStep: setCreateStep,
        onProgress: setUploadProgress,
        isCancelled: () => cancelRequested.current,
      });
      // The bridge page takes over the same share screen and fills in the link.
      void navigate(path, { state: { justCreated: true, flow: flowOf(kind) } });
    } catch (cause) {
      setStage('input');
      if (!(cause instanceof UploadCancelled)) {
        // the banner on the screen is the one place the error shows, so no notice on top of it
        setError(describeError(cause, kind === 'chat' ? t('createBridge.actionChat') : kind === 'receive' ? t('homeKinds.titleReceive') : t('common.createBridge')));
      }
    } finally {
      setUploadProgress(null);
      setCancelling(false);
    }
  }, [kind, stage, ready, ttl, views, file, secretText, navigate]);

  const cancelUpload = (): void => {
    cancelRequested.current = true;
    setCancelling(true);
  };

  return { stage, createStep, uploadProgress, cancelling, error, setError, create, cancelUpload };
}
